import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Button, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { Calculator, BookOpen, CheckCircle, AlertTriangle } from 'lucide-react';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
// PageTitle lives on the composite SemesterPage that mounts this section.
import * as payrollApi from '../../../../services/api/payrollApi';
import { toast } from 'sonner';

/**
 * SemesterskuldPage — per-employee semesterskuld preview & booking.
 *
 * Flow:
 *   1. Choose asOf date (default today).
 *   2. Preview liability → shows total 2940 target + per-employee breakdown.
 *   3. Preview adjustment → compares target with current 2940 balance,
 *      returns delta (debit 7290 / credit 2940 or the inverse).
 *   4. Book → POST adjustment. Idempotent: re-posting the same asOf is a no-op
 *      because target matches current balance after the first run.
 *
 * Backend: /payroll/semesterskuld/{liability|adjustment|book}
 */
const SemesterskuldSection = () => {
  const { companyId } = useParams();

  const [asOf, setAsOf] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [description, setDescription] = useState('');
  const [liability, setLiability] = useState(null);
  const [adjustment, setAdjustment] = useState(null);
  const [loadingLiability, setLoadingLiability] = useState(false);
  const [loadingAdjustment, setLoadingAdjustment] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState(null);

  const fmt = (n) => new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  const handleLiability = async () => {
    setLoadingLiability(true);
    setError(null);
    setAdjustment(null);
    try {
      const res = await payrollApi.getSemesterskuldLiability(companyId, asOf);
      setLiability(res.data?.data || res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte beräkna semesterskuld.');
    } finally {
      setLoadingLiability(false);
    }
  };

  const handleAdjustment = async () => {
    setLoadingAdjustment(true);
    setError(null);
    try {
      const res = await payrollApi.getSemesterskuldAdjustment(companyId, asOf);
      setAdjustment(res.data?.data || res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte beräkna justering.');
    } finally {
      setLoadingAdjustment(false);
    }
  };

  const handleBook = async () => {
    const delta = Math.abs(Number(adjustment?.delta) || 0);
    if (delta < 0.005) {
      toast.info('Ingen justering behövs — 2940 matchar redan beräknad skuld.');
      return;
    }
    const confirm = await Swal.fire({
      title: 'Bokföra semesterskuld?',
      html: (
        `<div style="text-align:left">`
        + `<p>Per <strong>${asOf}</strong>:</p>`
        + `<p>Beräknad skuld 2940: <strong>${fmt(adjustment.targetLiability)} kr</strong></p>`
        + `<p>Nuvarande saldo 2940: <strong>${fmt(adjustment.currentBalance)} kr</strong></p>`
        + `<p>Justering: <strong>${fmt(adjustment.delta)} kr</strong> (${adjustment.direction})</p>`
        + `<hr/>`
        + `<p style="font-size:0.85em;color:#6b7280">Verifikationen är idempotent — en andra körning på samma datum är en no-op.</p>`
        + `</div>`
      ),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Bokför',
      cancelButtonText: 'Avbryt',
    });
    if (!confirm.isConfirmed) return;

    setBooking(true);
    setError(null);
    try {
      const res = await payrollApi.bookSemesterskuld(companyId, {
        asOf,
        description: description || undefined,
      });
      const data = res.data?.data || res.data;
      toast.success(`Verifikation ${data.verificationNumber || data.verificationId} skapad.`);
      await handleAdjustment();
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte bokföra semesterskuld.');
    } finally {
      setBooking(false);
    }
  };

  return (
    <>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div className="card mb-3">
        <div className="card-body">
          <h4 className="mb-3">
            <Calculator size={20} className="me-2" />
            Semesterskuld (BAS 2940)
          </h4>
          <p className="text-muted" style={{ fontSize: 13 }}>
            Per-anställd skuld: (tjänade + överförda + sparade − använda) × daglön
            enligt <strong>procentregeln (§16a)</strong>. Motkonto 7290 (arbetare 7091).
            Rekommenderad kadens: bokslut (K2). Idempotent — samma datum skrivs aldrig över.
          </p>

          <Form.Group className="mb-3" style={{ maxWidth: 300 }}>
            <Form.Label>Per-datum (asOf)</Form.Label>
            <Form.Control
              type="date"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={handleLiability}
              disabled={loadingLiability}
              className="d-flex align-items-center gap-2"
            >
              {loadingLiability ? <Spinner size="sm" animation="border" /> : <Calculator size={16} />}
              1. Förhandsvisa skuld
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleAdjustment}
              disabled={loadingAdjustment || !liability}
              className="d-flex align-items-center gap-2"
            >
              {loadingAdjustment ? <Spinner size="sm" animation="border" /> : <AlertTriangle size={16} />}
              2. Förhandsvisa justering
            </Button>
            <Button
              variant="primary"
              onClick={handleBook}
              disabled={booking || !adjustment || Math.abs(Number(adjustment?.delta) || 0) < 0.005}
              className="d-flex align-items-center gap-2"
            >
              {booking ? <Spinner size="sm" animation="border" /> : <BookOpen size={16} />}
              3. Bokför
            </Button>
          </div>

          <Form.Group className="mt-3" style={{ maxWidth: 600 }}>
            <Form.Label className="small text-muted">Verifikationstext (valfri)</Form.Label>
            <Form.Control
              size="sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`Justering semesterskuld ${asOf}`}
            />
          </Form.Group>
        </div>
      </div>

      {liability && (
        <div className="card mb-3">
          <div className="card-body">
            <h5>Beräknad skuld per {liability.asOf || asOf}</h5>
            <div className="mb-2">
              <Badge bg="info" style={{ fontSize: 14 }}>
                Total skuld 2940: {fmt(liability.totalLiability)} kr
              </Badge>
            </div>
            {Array.isArray(liability.perEmployee) && liability.perEmployee.length > 0 && (
              <Table responsive hover size="sm">
                <thead>
                  <tr>
                    <th>Anställd</th>
                    <th className="text-end">Dagar kvar</th>
                    <th className="text-end">Daglön</th>
                    <th className="text-end">Soc. avg.</th>
                    <th className="text-end">Skuld</th>
                  </tr>
                </thead>
                <tbody>
                  {liability.perEmployee.map((r) => (
                    <tr key={r.employeeId}>
                      <td>{r.employeeName || r.employeeId}</td>
                      <td className="text-end">{fmt(r.liableDays)}</td>
                      <td className="text-end">{fmt(r.dailyValue)}</td>
                      <td className="text-end">{fmt(r.socialSecurity)}</td>
                      <td className="text-end"><strong>{fmt(r.liability)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      )}

      {adjustment && (
        <div className="card">
          <div className="card-body">
            <h5>Justering per {adjustment.asOf || asOf}</h5>
            <Table size="sm" borderless style={{ maxWidth: 500 }}>
              <tbody>
                <tr>
                  <td className="text-muted">Beräknad skuld 2940</td>
                  <td className="text-end">{fmt(adjustment.targetLiability)} kr</td>
                </tr>
                <tr>
                  <td className="text-muted">Nuvarande saldo 2940</td>
                  <td className="text-end">{fmt(adjustment.currentBalance)} kr</td>
                </tr>
                <tr>
                  <td><strong>Justering</strong></td>
                  <td className="text-end">
                    <strong>{fmt(adjustment.delta)} kr</strong>{' '}
                    {adjustment.direction && (
                      <Badge bg={adjustment.direction === 'increase' ? 'warning' : 'secondary'}>
                        {adjustment.direction === 'increase' ? 'Ökning' : 'Minskning'}
                      </Badge>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
            {Math.abs(Number(adjustment.delta) || 0) < 0.005 && (
              <Alert variant="success" className="d-flex align-items-center gap-2">
                <CheckCircle size={16} />
                Ingen justering behövs — 2940 matchar redan beräknad skuld.
              </Alert>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SemesterskuldSection;
